import { useCallback, useEffect, useRef, useState } from "react";
import type { VoiceAgentService } from "@/services/integrations/elevenlabs/VoiceAgentService";
import type { TranscriptMessage } from "@/pages/agent-interfaces/live-agent/components/TranscriptPanel";
import { DEFAULT_AGENT_VOICE_VOLUME } from "./useAudioSessionControls";
import type { ImageAttachment } from "./useImageAttachments";

/** The slice of useVoiceAgentConversation's return this hook needs. */
interface VoiceAgentLike {
  connected: boolean;
  isConnecting: boolean;
  getService: () => VoiceAgentService | null;
  startConversation: () => Promise<boolean>;
  sendUserMessage: (text: string) => void;
  sendContextualUpdate: (text: string) => void;
  getConversationId: () => string | null;
}

/** The slice of useAudioSessionControls' return this hook needs. */
interface AudioControlsLike {
  setIsMicMuted: (muted: boolean) => void;
  setIsAgentVoiceMuted: (muted: boolean) => void;
}

interface StagedImage {
  id: string;
  file: File;
  analysisText?: string;
  compressedBase64?: string | null;
}

interface UseTextChatSessionOptions {
  voiceAgent: VoiceAgentLike;
  /** Audio state setters — endTextChat restores both channels on the way out. */
  audio: AudioControlsLike;
  attachments: ImageAttachment[];
  removeAttachment: (id: string) => void;
  stageImages: (attachments: StagedImage[], conversationId: string | null) => void;
  endConversationAfterUploads: () => Promise<void>;
  setMessages: React.Dispatch<React.SetStateAction<TranscriptMessage[]>>;
  setShowTranscript: (show: boolean) => void;
  setConversationStartTime: (timestamp: number | null) => void;
  /** The agent's configured client_events — used to warn when text replies can't render. */
  clientEvents?: string[];
  /**
   * The embed silences mic + agent voice when a visitor opts into typing; the
   * full page deliberately leaves the mic live so they can speak mid-chat.
   */
  muteOnTextStart?: boolean;
  /** Full page tracks a per-conversation image cap via this callback. */
  onImagesStaged?: (count: number) => void;
}

/**
 * Owns the text-chat side of a voice session: the composer value, text-only /
 * started-from-text mode flags, the optimistic-send flow (typed message and
 * image bubbles render locally before the agent echoes anything), and ending
 * a text chat. Shared by the standalone live-agent page and the embed.
 *
 * `isTextInitiatedRef` is exposed (not just state) because the transcript
 * subscription reads it mid-callback to skip echoing optimistic user messages.
 */
export const useTextChatSession = ({
  voiceAgent,
  audio,
  attachments,
  removeAttachment,
  stageImages,
  endConversationAfterUploads,
  setMessages,
  setShowTranscript,
  setConversationStartTime,
  clientEvents,
  muteOnTextStart = false,
  onImagesStaged,
}: UseTextChatSessionOptions) => {
  const [inlineMessage, setInlineMessage] = useState("");
  const [isTextOnlyMode, setIsTextOnlyMode] = useState(false);
  const [startedFromText, setStartedFromText] = useState(false);
  // Tracks whether the current session was started from text input (optimistic
  // user messages already pushed by sendInlineMessage — skip duplicates from onMessage).
  const isTextInitiatedRef = useRef(false);
  const optimisticUserMessagesRef = useRef<string[]>([]);

  // Reset text-mode flags when the conversation disconnects.
  useEffect(() => {
    if (!voiceAgent.connected) {
      setIsTextOnlyMode(false);
      setStartedFromText(false);
      isTextInitiatedRef.current = false;
      optimisticUserMessagesRef.current = [];
    }
  }, [voiceAgent.connected]);

  /**
   * Start a text-only conversation from scratch: ElevenLabs textOnly overrides,
   * the conversation itself, then the mode flags + a clean transcript. Returns
   * false when the conversation failed to start so callers can bail before
   * pushing optimistic UI. `force` skips the already-connected guard — the
   * noise-switch path ends a voice call and immediately restarts as text,
   * before React has flushed the disconnected state.
   */
  const beginTextSession = useCallback(
    async ({ force = false }: { force?: boolean } = {}): Promise<boolean> => {
      if (!force && (voiceAgent.connected || voiceAgent.isConnecting)) return true;
      const service = voiceAgent.getService();
      if (service) {
        service.setOverrides({
          conversation: { textOnly: true },
          turn: { turn_timeout: 600, silence_end_call_timeout: 600 },
        });
      }
      let started: boolean;
      try {
        started = await voiceAgent.startConversation();
      } catch (error) {
        console.error("Error starting text conversation:", error);
        return false;
      }
      if (!started) return false;
      if (muteOnTextStart) {
        // Visitor opted into typing — silence both channels. They can unmute
        // either side from the action row if they decide to talk after all.
        try {
          const svc = voiceAgent.getService();
          if (svc) {
            await svc.setMicMuted(true);
            await svc.setVolume(0);
          }
        } catch (error) {
          console.warn("Could not auto-mute after text start:", error);
        }
        audio.setIsMicMuted(true);
        audio.setIsAgentVoiceMuted(true);
      }
      setIsTextOnlyMode(true);
      setStartedFromText(true);
      setShowTranscript(true);
      isTextInitiatedRef.current = true;
      // Clear old transcript when starting a new conversation from text
      setMessages([]);
      setConversationStartTime(Date.now());
      return true;
    },
    [
      voiceAgent,
      muteOnTextStart,
      audio,
      setMessages,
      setShowTranscript,
      setConversationStartTime,
    ],
  );

  // Attachments currently being delivered, so the auto-delivery effect and a
  // manual send never hand the same image to the Genie twice.
  const deliveringIdsRef = useRef<Set<string>>(new Set());

  /**
   * Hand attachments to a connected conversation: the analysis goes in as
   * context, the images render in the transcript and are staged for upload.
   * Without typed text alongside, a short user message is sent too — context
   * alone never prompts a reply, so the Genie would otherwise sit silent.
   */
  const deliverAttachments = useCallback(
    async (sentAttachments: ImageAttachment[], hasTypedText: boolean) => {
      if (sentAttachments.length === 0) return;

      const analysisContext = sentAttachments
        .filter((attachment) => attachment.status === "ready" && attachment.analysisText?.trim())
        .map((attachment) => attachment.analysisText!.trim())
        .join("\n\n");

      if (analysisContext) {
        voiceAgent.sendContextualUpdate(
          `User attached images with the following descriptions:\n\n${analysisContext}`,
        );
      }

      const dataUrls = await Promise.all(
        sentAttachments.map(
          (att) =>
            new Promise<string | null>((resolve) => {
              const reader = new FileReader();
              reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : null);
              reader.onerror = () => resolve(null);
              reader.readAsDataURL(att.file);
            }),
        ),
      );

      const imageMessage: TranscriptMessage = {
        message: "",
        isUser: true,
        timestamp: new Date(),
        imageAttachments: sentAttachments.map((att, index) => ({
          id: att.id,
          previewUrl: dataUrls[index] ?? att.previewUrl,
          analysisText: att.analysisText,
        })),
      };
      setMessages((prev) => [...prev, imageMessage]);

      if (!hasTypedText) {
        const promptText = analysisContext
          ? sentAttachments.length === 1
            ? "I've attached a photo."
            : "I've attached some photos."
          : "I attached photos for the team. Please ask me what details they should review.";
        // The image bubble already shows what was sent — skip the echoed text.
        optimisticUserMessagesRef.current.push(promptText);
        voiceAgent.sendUserMessage(promptText);
      }

      stageImages(
        sentAttachments.map((att) => ({
          id: att.id,
          file: att.file,
          analysisText: att.analysisText,
          compressedBase64: att.compressedBase64,
        })),
        voiceAgent.getConversationId(),
      );
      onImagesStaged?.(sentAttachments.length);
      sentAttachments.forEach((attachment) => {
        removeAttachment(attachment.id);
        deliveringIdsRef.current.delete(attachment.id);
      });
    },
    [voiceAgent, setMessages, stageImages, onImagesStaged, removeAttachment],
  );

  // Mid-conversation, an image reaches the Genie as soon as its analysis
  // settles — a visitor talking by voice never presses send.
  useEffect(() => {
    if (!voiceAgent.connected) return;
    const pending = attachments.filter((attachment) => !deliveringIdsRef.current.has(attachment.id));
    if (pending.length === 0 || pending.some((attachment) => attachment.status === "analyzing")) return;
    pending.forEach((attachment) => deliveringIdsRef.current.add(attachment.id));
    void deliverAttachments(pending, false);
  }, [attachments, voiceAgent.connected, deliverAttachments]);

  /**
   * Send the composer's contents — or `override`, when a caller supplies the
   * text itself (a seeded first question from a Genie Page never passes
   * through `inlineMessage`, so there is nothing in state to read).
   */
  const sendInlineMessage = useCallback(
    async (override?: string) => {
      const trimmed = (override ?? inlineMessage).trim();
      const sentAttachments = attachments.filter((attachment) => !deliveringIdsRef.current.has(attachment.id));
      if (!trimmed && sentAttachments.length === 0) return;
      // Claimed before the session starts, so auto-delivery can't take them once it connects.
      sentAttachments.forEach((attachment) => deliveringIdsRef.current.add(attachment.id));
      const releaseAttachments = () =>
        sentAttachments.forEach((attachment) => deliveringIdsRef.current.delete(attachment.id));

      // If the configured client_events array doesn't include `agent_response`,
      // the Genie's text reply will NEVER reach us — it's an agent-config
      // setting on the ElevenLabs side, not a client bug. Warn on send so a
      // silent chat is diagnosable.
      if (!clientEvents || !clientEvents.includes("agent_response")) {
        console.warn(
          "[TextChat] This agent's client_events does NOT include `agent_response`. " +
            "Text replies from the Genie will not appear in the chat. " +
            "Fix: open this agent's settings and enable `agent_response` (and `user_transcript`) in Client Events.",
        );
      }

      if (!voiceAgent.connected && !voiceAgent.isConnecting) {
        const started = await beginTextSession();
        if (!started) {
          releaseAttachments();
          return;
        }
      } else if (voiceAgent.connected && !isTextOnlyMode) {
        // Already in a voice session — open the chat panel so the typed reply
        // and the Genie's response render somewhere.
        setIsTextOnlyMode(true);
        isTextInitiatedRef.current = true;
        setShowTranscript(true);
      }

      await deliverAttachments(sentAttachments, !!trimmed);

      if (trimmed) {
        // Local optimistic push so the typed message renders immediately,
        // independent of whether ElevenLabs echoes user_transcript back.
        const userMessage = { message: trimmed, isUser: true, timestamp: new Date() };
        setMessages((prev) => [...prev, userMessage]);
        optimisticUserMessagesRef.current.push(trimmed);
        voiceAgent.sendUserMessage(trimmed);
      }

      setInlineMessage("");
    },
    [
      inlineMessage,
      attachments,
      clientEvents,
      voiceAgent,
      isTextOnlyMode,
      beginTextSession,
      setShowTranscript,
      setMessages,
      deliverAttachments,
    ],
  );

  const endTextChat = useCallback(async () => {
    try {
      const service = voiceAgent.getService();
      if (service) {
        await service.setVolume(DEFAULT_AGENT_VOICE_VOLUME);
        await service.setMicMuted(false);
      }
    } catch (error) {
      console.error("Error resetting voice output/mic before end chat", error);
    }
    try {
      await endConversationAfterUploads();
    } catch (error) {
      console.error("Error ending text chat conversation", error);
    }
    setShowTranscript(false);
    setInlineMessage("");
    setStartedFromText(false);
    setIsTextOnlyMode(false);
    audio.setIsMicMuted(false);
    audio.setIsAgentVoiceMuted(false);
    isTextInitiatedRef.current = false;
    optimisticUserMessagesRef.current = [];
  }, [voiceAgent, endConversationAfterUploads, audio, setShowTranscript]);

  return {
    inlineMessage,
    setInlineMessage,
    isTextOnlyMode,
    setIsTextOnlyMode,
    startedFromText,
    setStartedFromText,
    isTextInitiatedRef,
    optimisticUserMessagesRef,
    beginTextSession,
    sendInlineMessage,
    endTextChat,
  };
};
